/**
 * AgentConversationListView
 *
 * Full-page conversation list view with filtering, search, and bulk actions
 * Replaces sidebar-only filtering system with dedicated route-based views
 *
 * Routes:
 * - /en/agent/conversations - All conversations
 * - /en/agent/favorites - Favorited conversations only
 * - /en/agent/archived - Archived conversations only
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import useAgentSessionList from '../../../components/agent/hooks/useAgentSessionList';
import { useConfirmDialog } from '../../../components/dialogs/ConfirmDialog';
import AgentConversationListHeader from '../../../components/agent/conversation-list/AgentConversationListHeader';
import AgentConversationListGroup from '../../../components/agent/conversation-list/AgentConversationListGroup';
import AgentConversationListItem from '../../../components/agent/conversation-list/AgentConversationListItem';
import AgentLayout from '../../../components/agent/layout/AgentLayout';
import { paths, routes } from '../../../utils/constants/routes';
import { MAX_SIDEBAR_RECENT_CHATS } from '../../../utils/constants/agent';

const AgentConversationListView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showConfirm } = useConfirmDialog();
  const { allSessions, updateSession, deleteSession, isLoading } = useAgentSessionList();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set()); // Gmail pattern: no mode toggle needed

  // Determine filter based on current route
  const getFilterType = () => {
    if (location.pathname.includes('/favorites')) return 'favorites';
    if (location.pathname.includes('/archived')) return 'archived';
    return 'all';
  };

  const filterType = getFilterType();

  // Filter conversations based on route and search
  const filteredConversations = useMemo(() => {
    let filtered = allSessions;

    // Apply route-based filter
    if (filterType === 'favorites') {
      filtered = filtered.filter((s) => s.isFavorite && !s.isArchived);
    } else if (filterType === 'archived') {
      filtered = filtered.filter((s) => s.isArchived);
    } else {
      // 'all' - exclude archived
      filtered = filtered.filter((s) => !s.isArchived);
    }

    // LIMITATION: Client-side title-only search
    // The previous popup modal had backend full-text search that included:
    // - Session titles AND message content (user and AI messages)
    // - Similarity scoring and match highlighting
    // TODO: Consider re-implementing backend search on this page
    // See AgentSearchContext.jsx and agentSessions.js for existing backend search infrastructure
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((s) => s.title?.toLowerCase().includes(query));
    }

    // Sort by lastActivity (most recent first)
    return filtered.sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
  }, [allSessions, filterType, searchQuery]);

  // Group conversations by time
  const groupedConversations = useMemo(() => {
    const now = new Date();
    const groups = {
      today: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      older: []
    };

    filteredConversations.forEach((conversation) => {
      const lastActivity = new Date(conversation.lastActivity);
      const diffDays = Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        groups.today.push(conversation);
      } else if (diffDays === 1) {
        groups.yesterday.push(conversation);
      } else if (diffDays <= 7) {
        groups.lastWeek.push(conversation);
      } else if (diffDays <= 30) {
        groups.lastMonth.push(conversation);
      } else {
        groups.older.push(conversation);
      }
    });

    return groups;
  }, [filteredConversations]);

  // Selection handlers (Gmail pattern: always-visible checkboxes)
  const handleSelectionChange = useCallback((sessionId, isChecked) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(sessionId);
      } else {
        newSet.delete(sessionId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = new Set(filteredConversations.map((c) => c.id));
    setSelectedIds(allIds);
  }, [filteredConversations]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Bulk action handlers
  const handleBulkFavorite = useCallback(async () => {
    const promises = Array.from(selectedIds).map((sessionId) => {
      const session = allSessions.find((s) => s.id === sessionId);
      if (session) {
        return updateSession(sessionId, { is_favorite: true });
      }
      return Promise.resolve();
    });
    await Promise.all(promises);
    setSelectedIds(new Set());
  }, [selectedIds, allSessions, updateSession]);

  const handleBulkArchive = useCallback(async () => {
    const promises = Array.from(selectedIds).map((sessionId) => {
      const session = allSessions.find((s) => s.id === sessionId);
      if (session) {
        return updateSession(sessionId, { is_archived: true });
      }
      return Promise.resolve();
    });
    await Promise.all(promises);
    setSelectedIds(new Set());
  }, [selectedIds, allSessions, updateSession]);

  const handleBulkDelete = useCallback(async () => {
    const count = selectedIds.size;
    const confirmed = await showConfirm({
      title: 'Delete Conversations',
      message: `Are you sure you want to delete ${count} conversation${count > 1 ? 's' : ''}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (confirmed) {
      const promises = Array.from(selectedIds).map((sessionId) => deleteSession(sessionId));
      await Promise.all(promises);
      setSelectedIds(new Set());
    }
  }, [selectedIds, deleteSession, showConfirm]);

  // Action handlers
  const handleConversationClick = useCallback(
    (sessionId) => {
      navigate(`${paths.AGENT}/chat/${sessionId}`);
    },
    [navigate]
  );

  const handleFavorite = useCallback(
    async (sessionId) => {
      const session = allSessions.find((s) => s.id === sessionId);
      if (session) {
        await updateSession(sessionId, { is_favorite: !session.isFavorite });
      }
    },
    [allSessions, updateSession]
  );

  const handleArchive = useCallback(
    async (sessionId) => {
      const session = allSessions.find((s) => s.id === sessionId);
      if (session) {
        await updateSession(sessionId, { is_archived: !session.isArchived });
      }
    },
    [allSessions, updateSession]
  );

  const handleDelete = useCallback(
    async (sessionId) => {
      const session = allSessions.find((s) => s.id === sessionId);
      if (!session) return;

      const confirmed = await showConfirm({
        title: 'Delete Conversation',
        message: `Are you sure you want to delete "${session.title}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        variant: 'danger'
      });

      if (confirmed) {
        await deleteSession(sessionId);
      }
    },
    [allSessions, deleteSession, showConfirm]
  );

  // Sidebar handlers
  const handleNewChat = () => {
    navigate(paths.AGENT);
  };

  const handleSearchConversations = () => {
    // Navigate to all conversations page (useful when on favorites/archived)
    navigate(paths.AGENT_CONVERSATIONS);
  };

  const handleBrowseByProject = () => {
    // Future: Browse by project functionality
  };

  const handleFavoritesClick = () => {
    navigate(paths.AGENT_FAVORITES);
  };

  const handleArchivedClick = () => {
    navigate(paths.AGENT_ARCHIVED);
  };

  const handleShowMoreRecentChats = () => {
    navigate(paths.AGENT_CONVERSATIONS);
  };

  const handleChatClick = (chatId) => {
    navigate(routes.AGENT_CHAT_PATH.replace(':chatId', chatId));
  };

  const handleRenameChat = async (chatId, newName) => {
    // If newName is not provided (legacy call from sidebar context menu), prompt user
    if (newName === undefined) {
      const session = allSessions.find((s) => s.id === chatId);
      if (!session) return;
      newName = prompt('Enter new name for this conversation:', session.title);
      if (!newName || newName.trim() === '') return;
    }

    try {
      await updateSession(chatId, { name: newName.trim() });
    } catch (error) {
      console.error('Failed to rename chat:', error);
      alert('Failed to rename conversation. Please try again.');
    }
  };

  const handleRename = async (conversationId, newName) => {
    await handleRenameChat(conversationId, newName);
  };

  const handleToggleChatFavorite = async (chatId) => {
    const session = allSessions.find((s) => s.id === chatId);
    if (session) {
      await updateSession(chatId, { is_favorite: !session.isFavorite });
    }
  };

  const handleArchiveChat = async (chatId) => {
    const session = allSessions.find((s) => s.id === chatId);
    if (session) {
      await updateSession(chatId, { is_archived: true });
    }
  };

  const handleDeleteChat = async (chatId) => {
    await handleDelete(chatId);
  };

  // Get recent chats for sidebar (first MAX_SIDEBAR_RECENT_CHATS, filtered by current view)
  const recentChats = useMemo(() => {
    return allSessions
      .filter((s) => !s.isArchived) // Sidebar shows non-archived only
      .slice(0, MAX_SIDEBAR_RECENT_CHATS)
      .map((s) => ({
        id: s.id,
        label: s.title,
        isFavorite: s.isFavorite || false,
        isArchived: s.isArchived || false
      }));
  }, [allSessions]);

  // Render group if it has conversations
  const renderGroup = (label, conversations) => {
    if (conversations.length === 0) return null;

    return (
      <AgentConversationListGroup key={label} label={label} count={conversations.length}>
        {conversations.map((conversation) => (
          <AgentConversationListItem
            key={conversation.id}
            conversation={conversation}
            onClick={handleConversationClick}
            onFavorite={handleFavorite}
            onArchive={handleArchive}
            onDelete={handleDelete}
            onRename={handleRename}
            isSelected={selectedIds.has(conversation.id)}
            onSelectionChange={handleSelectionChange}
            chatUrl={`${window.location.origin}${paths.AGENT}/chat/${conversation.id}`}
          />
        ))}
      </AgentConversationListGroup>
    );
  };

  if (isLoading) {
    return (
      <AgentLayout
        onNewChat={handleNewChat}
        onSearchConversations={handleSearchConversations}
        onBrowseByProject={handleBrowseByProject}
        onFavorites={handleFavoritesClick}
        onArchived={handleArchivedClick}
        onShowMoreRecentChats={handleShowMoreRecentChats}
        onChatClick={handleChatClick}
        onRenameChat={handleRenameChat}
        onToggleChatFavorite={handleToggleChatFavorite}
        onArchiveChat={handleArchiveChat}
        onDeleteChat={handleDeleteChat}
        recentChats={recentChats}
        activeRoute={location.pathname}
        showChatInput={false}
      >
        <div className="agent-conversation-list-view">
          <div className="agent-conversation-list-loading">Loading conversations...</div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout
      onNewChat={handleNewChat}
      onSearchConversations={handleSearchConversations}
      onBrowseByProject={handleBrowseByProject}
      onFavorites={handleFavoritesClick}
      onArchived={handleArchivedClick}
      onShowMoreRecentChats={handleShowMoreRecentChats}
      onChatClick={handleChatClick}
      onRenameChat={handleRenameChat}
      onToggleChatFavorite={handleToggleChatFavorite}
      onArchiveChat={handleArchiveChat}
      onDeleteChat={handleDeleteChat}
      recentChats={recentChats}
      activeRoute={location.pathname}
      showChatInput={false}
    >
      <div className="agent-conversation-list-view">
        <AgentConversationListHeader
          filterType={filterType}
          conversationCount={filteredConversations.length}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          selectedCount={selectedIds.size}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          allSelected={
            selectedIds.size === filteredConversations.length && filteredConversations.length > 0
          }
        />

        {selectedIds.size > 0 && (
          <div className="agent-conversation-list-bulk-toolbar">
            <div className="agent-conversation-list-bulk-count">{selectedIds.size} selected</div>
            <div className="agent-conversation-list-bulk-actions">
              {selectedIds.size === filteredConversations.length ? (
                <button onClick={handleDeselectAll} type="button">
                  Deselect All
                </button>
              ) : (
                <button onClick={handleSelectAll} type="button">
                  Select All
                </button>
              )}
              <button onClick={handleBulkFavorite} type="button">
                Favorite
              </button>
              <button onClick={handleBulkArchive} type="button">
                Archive
              </button>
              <button onClick={handleBulkDelete} type="button" className="danger">
                Delete
              </button>
            </div>
          </div>
        )}

        <div className="agent-conversation-list-content">
          {filteredConversations.length === 0 ? (
            <div className="agent-conversation-list-empty">
              {searchQuery ? 'No conversations match your search.' : 'No conversations yet.'}
            </div>
          ) : (
            <>
              {renderGroup('Today', groupedConversations.today)}
              {renderGroup('Yesterday', groupedConversations.yesterday)}
              {renderGroup('Last Week', groupedConversations.lastWeek)}
              {renderGroup('Last Month', groupedConversations.lastMonth)}
              {renderGroup('Older', groupedConversations.older)}
            </>
          )}
        </div>
      </div>
    </AgentLayout>
  );
};

export default AgentConversationListView;
